'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import NextImage from '@src/components/Shared/NextImage';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import HeroPrimary from '@src/components/Shared/HeroPrimary';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';

import TechForSport from '@src/components/Sections/TechForSport';
import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import ServiceOverview from '@src/components/Sections/ServiceOverview';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { OrganizationsPageClientPropTypes } from './OrganizationsPage.types';
import Testimonials from '@src/components/Sections/Testimonials';

const OrganizationsPageClient: React.FC<OrganizationsPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/organizations`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroPrimary
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#000000]'
        attr='hero-component'
        className='bg-background-primary! mb-[0rem] flex flex-col items-center justify-between overflow-visible! pt-[6rem] pb-[3rem] md:flex-row md:px-[4rem]! md:pt-[9.3rem] md:pb-[6.3rem] lg:h-[60rem]! lg:gap-[2rem] lg:pt-[11.2rem] lg:pb-[8.2rem] 2xl:h-[75rem]! 2xl:py-[9.6rem]'
        title={page?.hero?.items?.[0]?.title}
        bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        image={page?.hero?.items?.[0]?.mediaPrimary}
        titleClassName='text-[4rem] lg:text-[5.7rem] 2xl:tracking-[-0.2432rem] leading-[4.7rem] lg:leading-[6.9rem] md:max-w-[36.6rem] md:max-w-3xl lg:max-w-[50rem] 2xl:max-w-full 2xl:tracking-[-0.152rem] [&>br]:hidden md:[&>br]:block'
        imgClassName='overflow-hidden absolute! sm:relative! sm:overflow-visible! flex items-end md:items-center justify-end xl:max-h-[80%] top-[0] h-full md:w-[50%] opacity-[0.2] sm:opacity-[1] [&>img]:w-auto! md:[&>img]:max-w-[120%]! md:[&>img]:right-[0%] &>img]:lg:right-[0%] md:[&>img]:absolute!'
        bodyCopyClassName='[&>p]:text-[1.6rem] md:[&>p]:text-[1.8rem] lg:[&>p]:text-[2rem] 2xl:[&>p]:text-[2rem] [&>p]:leading-[150%] md:max-w-[34.2rem] md:max-w-[43.7rem] lg:max-w-[51.7rem] 2xl:max-w-[59rem] relative md:top-[0] mt-[1.6rem]'
        childrenClassName='xsm:max-w-full'
      />

      <ImageTextSecondary
        reversed
        id={page?.whatMatters?.items?.[0]?.scrollTo?.slice(1)}
        background='lg:bg-background-tertiary'
        className='overflow-visible! lg:px-[4rem] lg:py-[2rem]'
        imgSecondClassName='lg:w-[65%]!'
        pretitle={page?.whatMatters?.items?.[0]?.preTitle}
        title={page?.whatMatters?.items?.[0]?.title}
        imgURL={page?.whatMatters?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.whatMatters?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.whatMatters?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <TechForSport
        id={page?.leagueGrid?.items?.[0]?.scrollTo?.slice(1)}
        headers={page?.leagueGrid?.items?.[0]}
        data={page?.leagueGrid?.items?.[0]?.topicCollection?.items}
      />

      <ImageTextPrimary
        id={page?.classroom?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.classroom?.items?.[0]?.preTitle}
        title={page?.classroom?.items?.[0]?.title}
        imgURL={page?.classroom?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.url}
        imgAlt={page?.classroom?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.description}
        imgLink={page?.classroom?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}
        imgCaption={page?.classroom?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.classroom?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.classroom?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <NextImage
        id={page?.research?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.research?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        objectFit='contain'
        className='3xl:my-[12.5rem] my-[4.5rem] h-[45rem] lg:h-[70rem]'
      >
        <Container className='mb-[0]! flex h-full'>
          <div className='mx-auto my-auto max-h-[50rem] w-[97%] rounded-4xl bg-[#181818] p-[2rem] opacity-[0.9] md:w-[80%] md:p-[4rem] xl:w-[60%]'>
            <SectionHeader
              className='h-full'
              preTitleClassName='mb-[1.6rem] sm:text-center'
              pretitle={page?.research?.items?.[0]?.preTitle}
              title={page?.research?.items?.[0]?.title}
              titleClassName='text-center text-[2.2rem] text-left! w-full! leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] sm:text-center! md:leading-[3rem] md:text-[2.8rem] lg:text-[4.8rem] lg:leading-[5rem]'
              childrenClassName='relative 2xl:max-w-[unset]!'
            >
              <RenderBodyCopy
                className='text-body-copy--light mx-auto mt-[2.4rem] max-w-[61.7rem] text-left font-normal sm:text-center 2xl:mt-[3.2rem]'
                bodyCopy={page?.research?.items?.[0]?.bodyCopy?.json?.content}
              />
              <RenderButtons className='justify-center' items={page?.research?.items?.[0]?.buttonCollection?.items} />
            </SectionHeader>
          </div>
        </Container>
      </NextImage>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <ImageTextPrimary
        reversed
        id={page?.personalized?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.personalized?.items?.[0]?.preTitle}
        title={page?.personalized?.items?.[0]?.title}
        imgURL={page?.personalized?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.personalized?.items?.[0]?.mediaPrimary?.description}
        imgLink={page?.personalized?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}
        imgCaption={page?.personalized?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.personalized?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.personalized?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextPrimary
        id={page?.respond?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.respond?.items?.[0]?.preTitle}
        title={page?.respond?.items?.[0]?.title}
        imgURL={page?.respond?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.respond?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.respond?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextPrimary
        reversed
        id={page?.platform?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.platform?.items?.[0]?.preTitle}
        title={page?.platform?.items?.[0]?.title}
        imgURL={page?.platform?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.platform?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.platform?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <NextImage
        id={page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.weExist?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[6.5rem]'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            titleHighlightBold
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExist?.items?.[0]?.title}
            titleClassName='text-left text-[2.2rem] w-full leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] sm:text-center md:leading-[3rem] md:text-[2.8rem] lg:max-w-[55rem] 2xl:max-w-[78rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            childrenClassName='relative w-full'
            align='center'
          >
            <RenderBodyCopy
              className='text-body-copy--light mx-auto mt-[2.4rem] max-w-[51.7rem] text-left sm:text-center 2xl:mt-[3.2rem] 2xl:max-w-[78.2rem]'
              bodyCopy={page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='justify-center' items={page?.weExist?.items?.[0]?.buttonCollection?.items} />
          </SectionHeader>
        </Container>
      </NextImage>

      <Container className='w-full'>
        <NextImage
          className='hidden sm:hidden md:block lg:block xl:block'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width}
        />
        <NextImage
          className='block sm:block md:hidden lg:hidden xl:hidden'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width}
        />
      </Container>

      <ServiceOverview
        id={page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
        description={page?.clientSuccess?.items?.[0]?.bodyCopy}
        services={page?.clientSuccess?.items?.[0]?.topicCollection?.items}
      />

      <CallToAction
        attr='call-to-action-component'
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default OrganizationsPageClient;
